import { Menu } from 'react-contexify';
import 'react-tooltip/dist/react-tooltip.css';
import {
  AUTHOR_MENU_ID,
  CONTEXT_MENU_ID,
  DELETE_FILTER_WARNING_ID,
  FILTER_SETTINGS_STATE,
  MARKET_MENU_ID,
  NONE_MENU_ID,
  REMINDER_MENU_ID,
  SIDE_BAR_TREE,
  STATUS_FILTER_MENU_ID,
  STATUS_MENU_ID,
  TAB_ACTION_MENU_ID,
  TAB_VIEW_MENU_ID,
  TAB_VIEW_SHARE_DOC_ID,
} from '../../common/constants';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  selectIsFilterDeletePopup,
  selectMenu,
  setMenu,
  setIsTabContextMenuActive,
  selectTabSharePopup,
  setShowTabSharePopup,
} from '../../redux/reducers/popupSlice';
import Author from '../popup/author/Author';
import LeaseFilter from '../popup/filter/LeaseFilter';
import LeaseStatus from '../popup/lease-status/LeaseStatus';
import Market from '../popup/market/Market';
import TabAction from '../popup/tab-action/TabAction';
import TabList from '../popup/tab-list/TabList';
import './content.scss';
import TabContainer from './tab/tab-container';
import Table from './table/Table';
import { selectIsAppInitialized } from '../../redux/reducers/appSlice';
import RowContextMenu from '../popup/row-context-menu/RowContextMenu';
import {
  selectFilterDialogState,
  selectSelectedTabFilterInfo,
} from '../../redux/reducers/tabFilterSlice';
import TabFilter from './tab/tab-filter-v2/tab-filter';
import DeleteTabWarning from '../popup/deleteTab/Delete-Tab-Warning';
import TabNameDialog from '../dialog/settings/tab-settings/tab-modal/TabNameDialog';
import TabShareModal from '../popup/tab-action/TabShareModal';
import { useEffect, useRef, useState } from 'react';
import {
  selectIsDashboardResizing,
  selectTabFilterEditState,
  setIsDashboardResizing,
  setPivotSelectedText,
  setSideBarSelectedTree,
  setTabFilterEditMode,
} from '../../redux/reducers/dashboardSlice';
import SidebarHide from '../../assets/images/dashboard/table/sidebar/SidebarHide.svg';
import SidebarUnhide from '../../assets/images/dashboard/table/sidebar/SidebarUnhide.svg';
import ReminderMenu from '../popup/reminderMenu/ReminderMenu';
import classNames from 'classnames';
import SideBarV2 from './sidebar/SideBarV2';

function Content() {
  const [isSidebarOpen, setSidebarState] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(264); // default width
  const [isResizing, setIsResizing] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const sideBarRef = useRef<HTMLDivElement>(null);

  const selectedMenu = useAppSelector(selectMenu);
  const showFilterPopup = useAppSelector(selectIsFilterDeletePopup);
  const isAppInitialized = useAppSelector(selectIsAppInitialized);
  const dispatch = useAppDispatch();
  const selectedTabFilter = useAppSelector(selectSelectedTabFilterInfo);
  const filterDialogState = useAppSelector(selectFilterDialogState);
  const showTabSharePopup = useAppSelector(selectTabSharePopup);
  const tabShareModalRef = useRef<HTMLDivElement>(null);
  const isTabFilterInEditMode = useAppSelector(selectTabFilterEditState);
  const isDashboardResizing = useAppSelector(selectIsDashboardResizing);

  // ESC and click handling
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (selectedMenu == TAB_VIEW_SHARE_DOC_ID) {
          dispatch(setShowTabSharePopup(false));
        }
      }
    };

    const handleMouseClick = (event: MouseEvent) => {
      if (selectedMenu == TAB_VIEW_SHARE_DOC_ID) {
        if (tabShareModalRef.current && !tabShareModalRef.current.contains(event.target as Node)) {
          dispatch(setShowTabSharePopup(false));
        }
      }
      const targetEl = event.target as HTMLElement;
      if (isTabFilterInEditMode && targetEl?.getAttribute('type') !== 'text') {
        dispatch(setTabFilterEditMode(false));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousedown', handleMouseClick);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousedown', handleMouseClick);
    };
  }, [selectedMenu, isTabFilterInEditMode]);

  // Resizing handlers
  const handleMouseDown = () => {
    setIsResizing(true);
    dispatch(setIsDashboardResizing(true));
    document.body.style.userSelect = 'none';
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing || !containerRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const newWidth = e.clientX - containerRect.left;
    if (newWidth > 180 && newWidth < 800) setSidebarWidth(newWidth);
  };

  const handleMouseUp = () => {
    setIsResizing(false);
    dispatch(setIsDashboardResizing(false));
    document.body.style.userSelect = '';
  };

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  return (
    <div
      className={classNames('content-container', { 'is-resizing': isResizing })}
      ref={containerRef}>
      {isAppInitialized && (
        <div className='data-panel-container'>
          <div
            id='left-container'
            ref={sideBarRef}
            style={{
              width: isSidebarOpen ? `${sidebarWidth}px` : '0px',
              transition: isResizing ? 'none' : 'width 0.2s ease',
            }}>
            {isSidebarOpen && <SideBarV2 containerWidth={sidebarWidth} parentRef={sideBarRef} />}
          </div>
          {!isSidebarOpen && (
            <div
              className={classNames('sideBar-icon-container', {
                'sideBar-expanded': isSidebarOpen,
                'sideBar-closed': !isSidebarOpen,
              })}>
              <img
                src={isSidebarOpen ? SidebarHide : SidebarUnhide}
                className='sidebar-hide-icon'
                onClick={() => {
                  setSidebarState(!isSidebarOpen);
                  if (!isSidebarOpen) {
                    dispatch(setSideBarSelectedTree(SIDE_BAR_TREE.INBOX));
                    dispatch(setPivotSelectedText(SIDE_BAR_TREE.INBOX));
                  }
                }}
              />
            </div>
          )}
          {/* 🔸 Divider between left and right container */}
          {isSidebarOpen && (
            <div
              className='resize-divider'
              onMouseDown={handleMouseDown}
              style={{
                cursor: 'col-resize',
                left: `${sidebarWidth - 2}px`,
              }}>
              <div className={classNames('sideBar-expanded')}>
                <img
                  src={SidebarHide}
                  className='sidebar-hide-icon'
                  draggable='false'
                  onClick={() => {
                    setSidebarState(!isSidebarOpen);
                    if (!isSidebarOpen) {
                      dispatch(setSideBarSelectedTree(SIDE_BAR_TREE.INBOX));
                      dispatch(setPivotSelectedText(SIDE_BAR_TREE.INBOX));
                    }
                  }}
                />
              </div>
            </div>
          )}
          <div
            id='right-container'
            style={{
              width: isSidebarOpen ? `calc(100% - ${sidebarWidth}px)` : '100%',
            }}
            className={classNames('table-main-container', {
              'sideBar-expanded': isSidebarOpen,
              'sideBar-closed': !isSidebarOpen,
              'dashboard-resize-inprogress': isDashboardResizing,
            })}>
            <Table />

            <Menu
              className='custom-context-menu'
              id={selectedMenu}
              theme='menu'
              animation='scale'
              onVisibilityChange={(isVisible: boolean) => {
                if (!isVisible) {
                  dispatch(setMenu(NONE_MENU_ID));
                  dispatch(setIsTabContextMenuActive(false));
                } else {
                  dispatch(setIsTabContextMenuActive(true));
                }
              }}>
              {selectedMenu == STATUS_MENU_ID && <LeaseStatus key={STATUS_MENU_ID} />}
              {selectedMenu == MARKET_MENU_ID && <Market key={MARKET_MENU_ID} />}
              {selectedMenu == AUTHOR_MENU_ID && <Author key={AUTHOR_MENU_ID} />}
              {selectedMenu == STATUS_FILTER_MENU_ID && <LeaseFilter key={STATUS_FILTER_MENU_ID} />}
              {selectedMenu == TAB_ACTION_MENU_ID && <TabAction key={TAB_ACTION_MENU_ID} />}
              {selectedMenu == TAB_VIEW_MENU_ID && <TabList key={TAB_VIEW_MENU_ID} />}
              {selectedMenu == CONTEXT_MENU_ID && <RowContextMenu key={CONTEXT_MENU_ID} />}
              {selectedMenu == REMINDER_MENU_ID && <ReminderMenu key={REMINDER_MENU_ID} />}
            </Menu>

            {isAppInitialized && <TabContainer />}

            {showTabSharePopup && (
              <div ref={tabShareModalRef}>
                <TabShareModal key={TAB_VIEW_SHARE_DOC_ID} />
              </div>
            )}

            {showFilterPopup && (
              <div className='tab-filter-delete-warning'>
                <DeleteTabWarning key={DELETE_FILTER_WARNING_ID} />
              </div>
            )}

            {filterDialogState === FILTER_SETTINGS_STATE.FILTER_SETTINGS && (
              <div className='tab-filter-settings'>
                <TabFilter />
              </div>
            )}

            {filterDialogState === FILTER_SETTINGS_STATE.FILTER_RENAME && (
              <div className='tab-filter-rename'>
                <TabNameDialog />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Content;
